import type { KeyboardEvent } from "react";
import type { StepKey, StepView } from "./pipeline";

interface Props {
  steps: StepView[];
  active: StepKey;
  onSelect: (step: StepKey) => void;
}

/** The job's documents as six numbered steps -- Draft BOQ, Supplier quote, Supplier PO,
 * Customer BOQ, Customer PO, Invoice -- each saying where it stands and when.
 * The chosen one is underlined and its document shows below. It replaces the
 * tab strip (Fix #11, #19): the steps share the width evenly and wrap onto a
 * second row on a narrow screen instead of running off the card.
 *
 * Still a tablist to assistive technology, because that is what it does:
 * arrow keys move between steps, and each step controls the panel below. */
export default function StepPipeline({ steps, active, onSelect }: Props) {
  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const by = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (by === 0) {
      return;
    }
    event.preventDefault();
    const index = steps.findIndex((step) => step.key === active);
    const next = steps[(index + by + steps.length) % steps.length];
    onSelect(next.key);
    document.getElementById(`step-${next.key}`)?.focus();
  }

  return (
    <div className="cm-pipeline" role="tablist" aria-label="The job's documents" onKeyDown={onKeyDown}>
      {steps.map((step, index) => {
        const selected = step.key === active;
        return (
          <button
            key={step.key}
            type="button"
            role="tab"
            id={`step-${step.key}`}
            aria-selected={selected}
            aria-controls="job-step-panel"
            tabIndex={selected ? 0 : -1}
            className={`cm-step ${step.state}${selected ? " active" : ""}`}
            onClick={() => onSelect(step.key)}
            data-testid={`step-${step.key}`}
          >
            <span className="cm-step-dot" aria-hidden="true">
              {step.state === "done" ? "✓" : index + 1}
            </span>
            <span className="cm-step-text">
              <span className="cm-step-label">{step.label}</span>
              <span className="cm-step-sub">{step.sub}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
